import { useState, useEffect } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';


const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [news, setNews] = useState([]);
  const [slides, setSlides] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
    fetchHeroSlides();
    fetchPodcasts();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await api.get(`/news`);
      setNews(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch news', error);
    }
  };

  const fetchPodcasts = async () => {
    try {
      const response = await api.get(`/podcasts`);
      setPodcasts(response.data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch podcasts', error);
    }
  };

  const fetchHeroSlides = async () => {
    try {
      const response = await api.get(`/hero-slides`);
      if (response.data.length > 0) {
        setSlides(response.data);
      } else {
        // Default slides if none exist
        setSlides([
          {
            id: 'default',
            title: 'Clarity | Experience | Confidence',
            subtitle: 'Professional legal services tailored to your needs',
            image_url: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            cta_text: 'Learn More',
            cta_link: '/about'
          }
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch hero slides', error);
      // Fallback to default slide on error
      setSlides([
        {
          id: 'default',
          title: 'Clarity | Experience | Confidence',
          subtitle: 'Professional legal services tailored to your needs',
          image_url: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
          cta_text: 'Learn More',
          cta_link: '/about'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    if (slides.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [slides.length]);

  return (
    <PageLayout>
      {/* Hero Section with Carousel */}
      <section className="relative h-[600px] overflow-hidden" data-testid="hero-section">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-primary">
            <p className="text-white font-sans text-lg">Loading...</p>
          </div>
        ) : (
          <>
            {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url("${(slide.image_data || slide.image_url || '').replace(/["\\)]/g, '')}")`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-primary/40 to-primary/80" />
            </div>
            <div className="relative h-full flex items-center justify-center text-center px-6">
              <div className="max-w-4xl">
                <h1 className="font-serif text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
                  {slide.title}
                </h1>
                <p className="font-sans text-xl md:text-2xl text-white/90 mb-8">
                  {slide.subtitle}
                </p>
                <Button
                  asChild
                  className="bg-accent text-accent-foreground hover:bg-accent/90 px-8 py-6 text-lg font-medium tracking-wide shadow-lg"
                  data-testid={`hero-cta-${index}`}
                >
                  <Link to={slide.cta_link}>{slide.cta_text}</Link>
                </Button>
              </div>
            </div>
          </div>
            ))}

            {slides.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-3 backdrop-blur-sm transition-all"
                  data-testid="carousel-prev"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="text-white" size={24} />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-3 backdrop-blur-sm transition-all"
                  data-testid="carousel-next"
                  aria-label="Next slide"
                >
                  <ChevronRight className="text-white" size={24} />
                </button>

                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        index === currentSlide ? 'bg-accent w-8' : 'bg-white/50'
                      }`}
                      data-testid={`carousel-dot-${index}`}
                      aria-label={`Go to slide ${index + 1}`}
                      aria-current={index === currentSlide}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </section>

      {/* Latest News Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-background-alt">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-primary text-center mb-12" data-testid="news-heading">
            Latest News
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {news.length > 0 ? (
              news.map((item, index) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="bg-white border border-border/40 p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group block"
                  data-testid={`news-card-${index}`}
                >
                  <p className="font-sans text-sm tracking-wide uppercase text-accent mb-2">
                    {item.category}
                  </p>
                  <h3 className="font-serif text-2xl font-medium text-primary mb-4 group-hover:text-accent transition-colors">
                    {item.title}
                  </h3>
                  <p className="font-sans text-base text-muted-foreground leading-relaxed mb-4">
                    {item.content.substring(0, 150)}...
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="font-sans text-sm text-muted-foreground">
                      {new Date(item.published_at).toLocaleDateString()}
                    </p>
                    <span className="font-sans text-sm text-accent group-hover:underline">
                      Read More →
                    </span>
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="font-sans text-lg text-muted-foreground">No news articles available</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="podcast-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-primary text-center mb-6">
            The Advocate's Voice
          </h2>
          <p className="font-sans text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto leading-relaxed">
            Listen to our podcast featuring insights from legal experts, case discussions, and community stories.
          </p>
          {podcasts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {podcasts.map((episode, index) => (
                <div
                  key={episode.id}
                  className="bg-background-alt border border-border/40 p-8"
                  data-testid={`podcast-episode-${index}`}
                >
                  <div className="flex items-baseline justify-between gap-4 mb-2">
                    <h3 className="font-serif text-2xl font-medium text-primary">
                      {episode.episode_number ? `Ep. ${episode.episode_number}: ` : ''}
                      {episode.title}
                    </h3>
                    {episode.duration ? (
                      <span className="font-sans text-sm text-muted-foreground whitespace-nowrap">
                        {episode.duration}
                      </span>
                    ) : null}
                  </div>
                  {episode.description ? (
                    <p className="font-sans text-base text-muted-foreground leading-relaxed mb-4">
                      {episode.description}
                    </p>
                  ) : null}
                  <audio
                    controls
                    preload="none"
                    className="w-full"
                    src={episode.audio_url}
                    data-testid={`podcast-audio-${index}`}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              ))}
            </div>
          ) : (
            <p className="font-sans text-lg text-muted-foreground text-center">
              New episodes coming soon.
            </p>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

export default Home;